<template>
  <v-container fluid class="pa-6 container-background">
    <v-form ref="form" v-model="formValid">
      
      <!-- HEADER -->
      <v-card class="main-card pa-6 mb-6">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <h1 class="main-title">
              <v-icon color="rgb(204 33 40)" class="mr-2">
                mdi-email-edit-outline
              </v-icon>
              Configuración del correo
            </h1>

            <p class="main-subtitle">
              Personaliza el contenido del correo que recibirán tus clientes.
            </p>
          </div>

        </div>
      </v-card>

      <v-row>
        <!-- FORMULARIO -->
        <v-col cols="12" md="7">
          
          <!-- INFORMACIÓN GENERAL -->
          <v-card class="section-card pa-6 mb-6">
            <div class="section-header">
              <div class="section-icon">
                <v-icon color="white">mdi-domain</v-icon>
              </div>

              <div>
                <h2 class="section-title">Información general</h2>
              </div>
            </div>

            <div class="mt-6">
              <label class="custom-label">
                Nombre de la compañía
              </label>

              <v-text-field
                outlined
                dense
                placeholder="Ej: HioPOS Reports"
                v-model="nameCompany"
                :rules="[rules.required]"
                class="custom-input"
              />
            </div>
          </v-card>

          <!-- CONTENIDO DEL CORREO -->
          <v-card class="section-card pa-6 mb-6">
            <div class="section-header">
              <div class="section-icon">
                <v-icon color="white">mdi-email-outline</v-icon>
              </div>

              <div>
                <h2 class="section-title">Contenido del correo</h2>
              </div>
            </div>

            <div class="mt-6">
              <label class="custom-label">
                Cuerpo del mensaje
              </label>

              <v-textarea
                outlined
                auto-grow
                rows="6"
                counter="3000"
                maxlength="3000"
                placeholder="Escribe aquí el contenido del correo..."
                v-model="messageContent"
                :rules="[rules.required]"
                class="custom-input"
              />
            </div>

            <div class="mt-4">
              <label class="custom-label">
                Texto resaltado para política de datos
              </label>

              <v-text-field
                outlined
                dense
                placeholder="Ej: clic aquí"
                v-model="highlightText"
                :rules="[rules.required]"
                class="custom-input"
              />
            </div>
          </v-card>

          <!-- POLÍTICA -->
          <v-card class="section-card pa-6 mb-6">
            <div class="section-header">
              <div class="section-icon">
                <v-icon color="white">mdi-shield-check-outline</v-icon>
              </div>

              <div>
                <h2 class="section-title">
                  Política de tratamiento de datos
                </h2>
              </div>
            </div>

            <div class="mt-6">
              <label class="custom-label">
                URL de política de datos
              </label>

              <v-text-field
                outlined
                dense
                placeholder="https://"
                v-model="policyUrl"
                :rules="[rules.required, rules.url]"
                class="custom-input"
              />

              <small class="helper-text">
                Este enlace será asignado automáticamente al texto resaltado.
              </small>
            </div>
          </v-card>

          <!-- CONTACTO -->
          <v-card class="section-card pa-6 mb-6">
            <div class="section-header">
              <div class="section-icon">
                <v-icon color="white">mdi-phone-outline</v-icon>
              </div>

              <div>
                <h2 class="section-title">Canales de contacto</h2>
              </div>
            </div>

            <div class="mt-6">
              <label class="custom-label">
                Correo o canal de contacto
              </label>

              <v-text-field
                outlined
                dense
                placeholder="correo@empresa.com"
                v-model="contact"
                :rules="[rules.required]"
                class="custom-input"
              />

              <small class="helper-text">
                Ingrese los canales de contacto separados por comas (,). 
                Ejemplo: correo@empresa.com, Bogotá: 6017775555, Cartagena: 6052224444
              </small>
            </div>
          </v-card>

          <!-- ALERT -->
          <v-alert
            v-if="showAlert"
            :type="alertType"
            border="left"
            colored-border
            elevation="2"
            class="mb-4"
          >
            {{ alertMessage }}
          </v-alert>

          <!-- BOTÓN -->
          <div class="d-flex justify-end">
            <v-btn
              color="rgb(204 33 40)"
              x-large
              elevation="2"
              rounded
              class="white--text px-10 font-weight-bold"
              @click="formUserByCompanySave"
            >
              <v-icon left>
                mdi-content-save-outline
              </v-icon>

              {{ isEditing
                ? "Actualizar configuración"
                : "Guardar configuración"
              }}
            </v-btn>
          </div>
        </v-col>

        <!-- PREVIEW -->
        <v-col cols="12" md="5">
          <v-card class="preview-card pa-5">
            <div class="d-flex align-center mb-4">
              <v-icon color="rgb(204 33 40)" class="mr-2">
                mdi-eye-outline
              </v-icon>

              <h3 class="preview-title">
                Vista previa
              </h3>
            </div>

            <div class="preview-email">
              <div class="preview-company">
                Sr(a)."Nombre Cliente"
              </div>

              <div class="preview-body">
                Le informamos que usted ha autorizado el tratamiento de sus datos personales, así como el uso con fines comerciales, 
                en el establecimiento {{ nameCompany || "'Nombre de la compañía'" }} en la fecha {{ new Date().toLocaleDateString('es-CO') }} a las {{ new Date().toLocaleTimeString('es-CO') }}.
              </div>

              <div class="preview-body mt-4">
                {{ messageContent || "Aquí aparecerá el contenido del correo..." }}
              </div>

              <div class="preview-contact mt-6">
                Este correo electrónico ha sido generado automáticamente. 
                Por favor, no responda a esta dirección de email.
              </div>
              
              <div class="d-flex">
                <div class="preview-body mt-6">
                  <strong>Canal de contacto:</strong>

                  <div
                    v-if="formattedContacts.length"
                    class="mt-2"
                  >
                    <div
                      v-for="(item, index) in formattedContacts"
                      :key="index"
                      class="contact-line"
                    >
                      {{ item }}
                    </div>
                  </div>

                  <div v-else class="mt-2">
                    correo@empresa.com
                  </div>
                </div>
                <div class="preview-body mt-6 ml-auto d-block">
                  <div class="preview-company text-center">
                    {{ nameCompany || "Nombre de la compañía" }}
                  </div>
                  <div class="text-center">
                    <img
                      :src="imageLogo"
                      alt="Logo"
                      class="logo-image"
                    />
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <!-- LOADING -->
    <v-overlay :value="loading" opacity="0.6" z-index="9999">
      <v-progress-circular
        indeterminate
        size="64"
        color="rgb(204 33 40)"
      />
    </v-overlay>
  </v-container>
</template>


<script>
import axios from "axios";
import { environment } from "@/data/environment";
export default {
  name: "ConfigEmailHabeasData",
  data() {
    return {
      formValid: false,
      logo: null,
      highlightText: "",
      policyUrl: "",
      loading: false,
      showAlert: false,
      alertMessage: "",
      alertType: "success",

      rules: {
        required: (v) => !!v || "Este campo es obligatorio",
        url: (v) => !v || /^(https?:\/\/)/.test(v) || "Debe ser una URL válida",
      },
      isEditing: false,
      imageLogo: '',
      existImage: false,
      originalData: {
        nameCompany: "",
        messageContent: "",
        highlightText: "",
        policyUrl: "",
        contact: false,
      },
      nameCompany: "",
      contact: "",
      messageContent: "",
      imageLogo: environment.HIOPOSWEBSERVICES + "/images/logo_pendiente.png"
    };
  },
  watch: {
    //
  },
  mounted() {
    this.getFormCompany();
  },
  computed: {
    formattedContacts() {

      if (!this.contact) {
        return [];
      }

      return this.contact
        .split(',')
        .map(item => item.trim())
        .filter(item => item);
    }
  },
  methods: {
    async getFormCompany() {
      try {
        this.loading = true;
        let idCompany = JSON.parse(
          atob(localStorage.getItem("uid"))
        ).idcompania;
        const response = await axios.get(
          `${environment.HIOPOSWEBSERVICES}/api/habeas-data/get-config-email/${idCompany}`
        );
        console.log(response);
        
        if (response.data.error) {
          this.setAlert("error", response.data.message);
        } else {
          const data = response?.data?.data;

          if (data && data.logo_path) {
            this.imageLogo = `${environment.HIOPOSWEBSERVICES}/storage/${data.logo_path}`;
          }

          if (data && data.config) {

            this.nameCompany = data.config.name_company ?? "";
            this.messageContent = data.config.message_content ?? "";
            this.highlightText = data.config.highlight_text ?? "";
            this.contact = data.config.contact ?? "";
            this.policyUrl = data.config.policy_url ?? "";
            this.isEditing = true;

            this.originalData = {
              nameCompany: this.nameCompany,
              messageContent: this.messageContent,
              highlightText: this.highlightText,
              policyUrl: this.policyUrl,
              contact: this.contact,
            };

          } else {

            this.isEditing = false;

          }
        }
      } catch (error) {
        console.error("Error al obtener el formulario:", error);
      } finally {
        this.loading = false;
      }
    },
    async formUserByCompanySave() {
      const isValid = this.$refs.form.validate();

      if (!isValid) {
        this.setAlert(
          "error",
          "Por favor complete todos los campos obligatorios."
        );
        return;
      }

      if (!this.hasChanges()) {
        this.setAlert(
          "info",
          "No hay cambios para guardar."
        );
        return;
      }
      this.loading = true;
      const formData = new FormData();
      formData.append("companyId", JSON.parse(atob(localStorage.getItem("uid"))).idcompania);
      formData.append("nameCompany", this.nameCompany);
      formData.append("messageContent", this.messageContent);
      formData.append("contact", this.contact);
      formData.append("highlightText", this.highlightText);
      formData.append("policyUrl", this.policyUrl);

      try {
        const response = await axios.post(
          `${environment.HIOPOSWEBSERVICES}/api/habeas-data/save-config-email`,
          formData
        );
        console.log(response);
        if (response.data.error) {
          this.setAlert("error", response.data.message);
        } else {
          this.getFormCompany()
          this.setAlert("success", response.data.message);
        }
      } catch (error) {
        console.error("Error guardando configuración:", error);
        this.setAlert("error", "Error al guardar la configuración.");
      } finally {
        this.loading = false;
      }
    },
    setAlert(type, message) {
      this.alertType = type;
      this.alertMessage = message;
      this.showAlert = true;
      setTimeout(() => (this.showAlert = false), 4000);
    },
    hasChanges() {

      if (!this.originalData) {
        return true;
      }

      if (this.nameCompany !== this.originalData.nameCompany) return true;
      if (this.messageContent !== this.originalData.messageContent) return true;
      if (this.highlightText !== this.originalData.highlightText) return true;
      if (this.policyUrl !== this.originalData.policyUrl) return true;
      if (this.contact !== this.originalData.contact) return true;

      return false
    }
  },
};
</script>


<style scoped>
  .container-background {
    /* background: #f8fafc; */
    min-height: 100vh;
  }

  .main-card {
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  }

  .main-title {
    font-size: 30px;
    font-weight: 700;
    /* color: #1e293b; */
    display: flex;
    align-items: center;
  }

  .main-subtitle {
    /* color: #6b7280; */
    margin-top: 4px;
    font-size: 14px;
  }

  .section-card {
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .section-icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
    background: rgb(204 33 40);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .section-title {
    font-size: 22px;
    font-weight: 700;
    /* color: #1e293b; */
    margin: 0;
  }

  .section-subtitle {
    font-size: 14px;
    /* color: #6b7280; */
    margin-top: 4px;
  }

  .custom-label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    /* color: #374151; */
  }

  .helper-text {
    /* color: #6b7280; */
    font-size: 12px;
  }

  .preview-card {
    position: sticky;
    top: 20px;
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  }

  .preview-title {
    font-size: 20px;
    font-weight: 700;
    /* color: #1e293b; */
  }

  .preview-email {
    background: #f9fafb;
    border-radius: 14px;
    padding: 20px;
    border: 1px solid #e5e7eb;
  }

  .preview-company {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 18px;
    color: #111827;
  }

  .preview-body {
    white-space: pre-line;
    line-height: 1.7;
    color: #374151;
    font-size: 14px;
  }

  .preview-link a {
    color: rgb(204 33 40);
    font-weight: 600;
    text-decoration: none;
  }

  .preview-contact {
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
    color: #374151;
    font-size: 14px;
  }

  ::v-deep .v-text-field--outlined fieldset {
    border-color: #d1d5db !important;
    border-radius: 12px;
  }

  ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: rgb(204 33 40) !important;
    border-width: 2px;
  }

  ::v-deep .v-input input,
  ::v-deep .v-input textarea {
    font-size: 14px;
  }

  ::v-deep .v-btn {
    letter-spacing: 0.3px;
    text-transform: none;
  }

  .logo-image {
    max-width: 180px;
    max-height: 120px;
    object-fit: contain;
  }
  .contact-line {
    margin-bottom: 6px;
    color: #4b5563;
    line-height: 1.5;
  }
</style>
